import type { BatchSession, Campaign, Pillar, ProductRow, SlateRow } from '../types/content'

export const planMeta = {
  title: 'Social Content Calendar',
  window: 'Aug 8 → Sep 30, 2026',
  today: 'Sat 8 Aug 2026',
  anchors: 125,
  slate: 168,
  total: 293,
  weeks: 8,
  cadence: 'X 4–6/day · LinkedIn 4–6/wk per page · Bluesky 3–5/wk · FB/IG/TikTok/YT 2–5/wk',
}

export const pillarTotals: { pillar: Pillar; slots: number; share: number; target: string }[] = [
  { pillar: 'Evergreen', slots: 142, share: 48.5, target: '40–50%' },
  { pillar: 'Product', slots: 94, share: 32.1, target: '30–40%' },
  { pillar: 'Campaign', slots: 57, share: 19.5, target: '15–25%' },
]

export const campaigns: Campaign[] = [
  {
    id: 'laravel',
    name: 'Laravel Free Weekend + Sale',
    heroStart: '2026-08-08',
    heroEnd: '2026-08-11',
    channels: 'Certs standard; amplify FN X',
    note: 'Revenue moment, already live. Build-up window is gone — hero opens on day one.',
  },
  {
    id: 'angular',
    name: 'Angular Workshop → live event',
    heroStart: '2026-08-22',
    heroEnd: '2026-08-28',
    secondStart: '2026-09-01',
    secondEnd: '2026-09-08',
    channels: 'Certs standard + BB LI; amplify FN',
    note: 'Two-peak arc across the month boundary. Angular is ~46% of revenue — heaviest framework weight of the period.',
  },
  {
    id: 'ai-cert',
    name: 'AI Certification rollout',
    heroStart: '2026-09-07',
    heroEnd: '2026-09-16',
    secondStart: '2026-09-25',
    secondEnd: '2026-09-30',
    channels: 'Unlearn X + LI and Certs standard',
    note: 'Biggest September story. Credential angle on Certs, AI-skills angle on Unlearn.',
  },
  {
    id: 'vue',
    name: 'Vue.js Free Weekend + Sale',
    heroStart: '2026-09-22',
    heroEnd: '2026-09-29',
    channels: 'Certs standard + Vue Cert LI; amplify VS, VJD, VJF, VJN',
    note: 'Recruitment opens Sep 9, hero in the final full week.',
  },
  {
    id: 'unlearn',
    name: 'Unlearn September Workshop',
    heroStart: '2026-09-04',
    heroEnd: '2026-09-10',
    secondStart: '2026-09-11',
    secondEnd: '2026-09-22',
    channels: 'Unlearn X + LI',
    note: 'Long recruitment window; front-loaded push then a steady drip to Sep 22.',
  },
  {
    id: 'nuxt',
    name: 'Nuxt Workshop recruitment',
    heroStart: '2026-09-19',
    heroEnd: '2026-09-30',
    channels: 'Certs standard + MN + NN BS; amplify VS, VJN',
    note: 'Workshop is Oct 6 — this period is build-up only, hero lands in October.',
  },
]

export const collisionNote =
  'AI Cert “Exams Open” (Sep 28) lands inside the Vue Free Weekend hero (Sep 26–29). Vue owns the Certificates.Dev channels Sep 26–29; AI Cert exams lead on Unlearn X + LinkedIn and cross to Certs from Sep 30.'

export const riskNote =
  'Six campaigns in eight weeks. The first pass came out at 50% campaign — double the healthy ceiling. Twelve redundant nudge slots were cut and the always-on slate is now counted, not assumed. If the slate gets skipped when things get busy, the effective split reverts to ~50% campaign.'

export const alwaysOn: SlateRow[] = [
  {
    slot: 'Daily exam-tip / dev-education post, rotating framework (Angular-weighted)',
    channels: 'Certs X',
    pillar: 'Evergreen',
    cadence: '~5×/week',
    total: 40,
  },
  { slot: 'AI-craft take — working well with AI, not tool news', channels: 'Unlearn X', pillar: 'Evergreen', cadence: '3×/week', total: 24 },
  { slot: 'Framework tip of the day', channels: 'VS X + VS BS', pillar: 'Evergreen', cadence: '3×/week', total: 24 },
  { slot: 'Nuxt tip', channels: 'MN X + MN BS', pillar: 'Evergreen', cadence: '2×/week', total: 16 },
  { slot: '“Why this certification exists” / use-case post', channels: 'Certs standard', pillar: 'Product', cadence: '3×/week', total: 24 },
  { slot: 'Cert-holder story or result spotlight', channels: 'Certs X + Certs FB', pillar: 'Product', cadence: '2×/week', total: 16 },
  { slot: 'B2B use case for engineering leaders', channels: 'BB LI', pillar: 'Product', cadence: '1×/week', total: 8 },
  { slot: 'Course / product highlight', channels: 'VS + MN channels', pillar: 'Product', cadence: '2×/week', total: 16 },
]

export const productRotation: ProductRow[] = [
  { product: 'Angular', standingSlot: 'Framework Friday (2 of 7) + workshop arc + first claim on the daily tip slate', coverage: 'Level with Vue in the recurring rotation; tip slate Angular-weighted to match ~46% revenue share', anchors: 17 },
  { product: 'Vue.js', standingSlot: 'Framework Friday (2 of 7) + Free Weekend hero + VS course', coverage: 'Heavy in September by campaign design, not by habit', anchors: 22 },
  { product: 'Laravel', standingSlot: 'Free Weekend hero (Aug 8–11) + 1 Framework Friday', coverage: 'Front-loaded', anchors: 9 },
  { product: 'Nuxt', standingSlot: '1 Framework Friday + workshop recruitment build', coverage: 'Late September', anchors: 9 },
  { product: 'React', standingSlot: '1 Framework Friday + async bootcamp', coverage: 'Steady', anchors: 3 },
  { product: 'JavaScript', standingSlot: '1 Framework Friday + async bootcamp', coverage: 'Steady', anchors: 4 },
  { product: 'Company Program', standingSlot: 'Certified Company Spotlight, Wednesdays on BitterBrains LinkedIn', coverage: 'Every week, 8 slots', anchors: 12 },
  { product: 'unlearn.dev', standingSlot: 'Workflow releases + workshop arc + AI-era takes', coverage: 'Every week', anchors: 14 },
  { product: 'AI Certification', standingSlot: 'Prep guide (Aug) → full rollout hero (Sep)', coverage: 'Ramps hard in September', anchors: 12 },
  { product: 'Boot Camps', standingSlot: 'Async bootcamp rollout announcements', coverage: 'Aug + Sep', anchors: 4 },
  { product: 'TypeScript Cert', standingSlot: 'Waitlist nudges', coverage: 'Blocked on launch date', anchors: 3 },
  { product: 'Tailored Trainings', standingSlot: '2 B2B slots on BitterBrains LinkedIn', coverage: 'Low volume, high intent', anchors: 2 },
]

export const watchItems = [
  {
    title: 'August Unlearn Workshop treated as cancelled',
    detail: 'Campaigns table marks it cancelled; the Webinars table still shows a launch Aug 11. No August recruitment arc planned — September is planned in full.',
    severity: 'flag' as const,
  },
  {
    title: 'Multi-language Certification is teaser only',
    detail: 'Unspecced with an Aug 1 start that has passed. Two teasers (Aug 19, Aug 26), both marked BLOCKED. Needs speccing before it can ship.',
    severity: 'blocked' as const,
  },
  {
    title: 'Vue.js Workshop (Aug 18) not planned',
    detail: 'Delayed, with a decision pending on whether it rolls up with the Nuxt workshop.',
    severity: 'flag' as const,
  },
  {
    title: 'TypeScript Certification — waitlist nudges only',
    detail: 'No launch date, no owner, no materials confirmed. Three light nudges across eight weeks.',
    severity: 'blocked' as const,
  },
  {
    title: 'Workflow 06 release dates are inconsistent',
    detail: 'Announcements planned Aug 12 / 19 / 26 in ascending order pending correction.',
    severity: 'flag' as const,
  },
  {
    title: 'Certified Developers LinkedIn excluded',
    detail: 'It is an output feed, not a marketing channel. All 21 slots re-routed to BitterBrains LinkedIn (~2–3 posts/week).',
    severity: 'note' as const,
  },
]

export const heavyDays = [
  { date: 'Sat 8 Aug', reason: 'Laravel Free Weekend opens' },
  { date: 'Tue 8 Sep', reason: 'Angular Workshop live' },
  { date: 'Mon 14 Sep', reason: 'AI Cert Cohort 1 opens' },
  { date: 'Sat 26 Sep', reason: 'Vue Free Weekend live' },
]

export const batchSessions: BatchSession[] = [
  {
    id: 's1',
    title: 'Session 1',
    window: 'by Fri Aug 14',
    due: '2026-08-14',
    covers: 'Aug 8 → Aug 23',
    items: [
      'Laravel Free Weekend + Sale asset set (launch, countdown, last-call, bridge)',
      'Vue School Vitest launch kit: card, clip, two short-form cuts',
      'Unlearn Workflow 06 release cards ×3 (reusable template)',
      'Certified Company Spotlight template — build once, runs eight times',
      'Framework Friday template — seven runs',
      'Quiz card template (question + answer pair)',
    ],
  },
  {
    id: 's2',
    title: 'Session 2',
    window: 'week of Aug 17',
    due: '2026-08-21',
    covers: 'Aug 22 → Sep 8',
    items: [
      'Angular Workshop asset set — the biggest single production job in this plan',
      'AI Cert Prep Guide lead-gen card, in both framings',
      'Async bootcamp product + roadmap cards',
      'Proof-point stat graphic (3,500+ / 7,000+ / 680+ / 140+)',
    ],
  },
  {
    id: 's3',
    title: 'Session 3',
    window: 'week of Sep 1',
    due: '2026-09-04',
    covers: 'Sep 7 → Sep 20',
    items: [
      'AI Certification rollout kit — explainer video + card family for all three stages',
      'Vue.js Free Weekend recruitment kit',
      'Nuxt Workshop launch graphic + agenda carousel',
      'Vitest short-form cut',
    ],
  },
  {
    id: 's4',
    title: 'Session 4',
    window: 'week of Sep 14',
    due: '2026-09-18',
    covers: 'Sep 21 → Oct 6',
    items: [
      'Vue Free Weekend + Sale hero set — live graphics, countdowns, prep, offer, last-call',
      'Nuxt workshop nudge set (carries into October)',
      'Month-close stat post',
      'October opening assets so the next cycle starts ahead',
    ],
  },
]

export const repurposing = [
  { hero: 'Angular Workshop explainer / clip', into: 'Certs launch post → BB LI corporate framing → X agenda thread → FN quote-repost → recap card → evergreen bootcamp clip' },
  { hero: 'AI Cert explainer video', into: 'Unlearn AI-skills cut → Certs credential cut → three stage-specific card variants → X thread on the assessment' },
  { hero: 'Vitest course lessons', into: 'VS course card → IG carousel → YT + TikTok shorts ×2 → VS/VJD LI lesson highlight → Unlearn AI-era testing take' },
  { hero: 'Certified Company Spotlight carousel', into: 'BB LI carousel → Certs X thread → stat graphic from the same data → Certs FB + BS trimmed' },
  { hero: 'Framework Friday thread', into: 'Certs X thread → FB + BS trimmed → community amplification matched to the framework → quiz card the next weekend' },
  { hero: 'Vue Free Weekend hero graphic', into: 'Certs + Vue Cert LI → VS full network → VJD/VJF/VJN → countdown and last-call variants' },
]

export const measurement = [
  {
    group: 'Per campaign',
    points: [
      'Free Weekend → Sale conversion, Laravel (Aug) vs Vue (Sep) — the cleanest comparison available.',
      'Workshop sign-ups per source channel: Angular (Certs-led) vs Unlearn (Unlearn-led) vs Nuxt (both).',
      'AI Cert rollout by stage, split by network — the single most valuable read of the period.',
    ],
  },
  {
    group: 'Per product',
    points: [
      'Does Angular content earn engagement proportional to its ~46% revenue share?',
      'Company Program: track LinkedIn engagement and inbound, not impressions.',
      'TypeScript waitlist adds per nudge — if they underperform, the answer is a launch date.',
    ],
  },
  {
    group: 'Per pillar & routing',
    points: [
      'Average engagement rate by pillar. Campaign share is at its ceiling — October comes down to 15–18%.',
      'Did ecosystem amplification add reach, or just add work? Weak channels get dropped from routing.',
    ],
  },
]

export const operatingNotes = [
  'Anchors cover roughly 60–70% of daily X posts. The rest is the standing playbook — replies, quote-posts, resurfaced evergreen tips. Don’t script it, but do staff it.',
  'On the four heaviest days assign a named person to work replies. A launch post with unanswered questions underneath costs more than it earns.',
  'October preview: Nuxt Workshop (Oct 6) hero opens Oct 1, and campaign share should drop back toward 15–18%.',
]
